import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Divider, Scroll, Stack } from '@/components/layout';
import { Collapsible, SvgIcon, Text } from '@/components/ui';
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import { GuardianProfileActionHeader } from '@/features/guardian-profile/components';
import { GuardianContactUsPopup } from '@/features/guardian-profile/screens/guardian-contact-us-screen';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const copy = STRINGS.guardian.support;
const overviewCopy = copy.articles.playerPathwayOverview;
const methodCopy = copy.articles.playerPathwayMethod;

type GuardianHelpAndSupportContentProps = {
  tabletPanel?: boolean;
};

type SupportArticleKey = 'overview' | 'method';

export function GuardianHelpAndSupportScreen() {
  const { isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('GuardianDashboard');
  }, [navigation]);

  return (
    <Stack flex={1} backgroundColor={colors['1']}>
      <GuardianProfileActionHeader
        title={copy.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={goBack}
      />
      <GuardianHelpAndSupportContent />
    </Stack>
  );
}

export function GuardianHelpAndSupportContent({
  tabletPanel = false,
}: GuardianHelpAndSupportContentProps) {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();
  const styles = useStyles();
  const [expandedArticle, setExpandedArticle] = useState<SupportArticleKey | null>('overview');
  const [contactOpen, setContactOpen] = useState(false);
  const bottomPadding = tabletPanel
    ? insets.bottom + scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN)
    : insets.bottom + scale(10);

  const renderChevron = useCallback(
    (isExpanded: boolean) => (
      <SvgIcon
        name="IcChevronRight"
        size={scale(20)}
        color={guardianColors['6']}
        style={[styles.chevron, isExpanded ? styles.chevronUp : styles.chevronDown]}
      />
    ),
    [scale, styles.chevron, styles.chevronDown, styles.chevronUp],
  );

  const handleContactPress = useCallback(() => {
    if (isTablet) {
      setContactOpen(true);
      return;
    }
    navigation.navigate('GuardianContactUs');
  }, [isTablet, navigation]);

  const handleOverviewOpenChange = useCallback((open: boolean) => {
    setExpandedArticle(open ? 'overview' : null);
  }, []);

  const handleMethodOpenChange = useCallback((open: boolean) => {
    setExpandedArticle(open ? 'method' : null);
  }, []);

  return (
    <Stack flex={1}>
      <Scroll
        style={styles.scroll}
        flex={1}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled">
        <Stack flex={1}>
          {tabletPanel ? (
            <Text variant="Font Guardian App/Heading/Heading 24" style={styles.screenTitle}>
              {copy.title}
            </Text>
          ) : null}

          <Stack backgroundColor={colors['0']} style={styles.articleCard}>
            <Collapsible
              open={expandedArticle === 'overview'}
              onOpenChange={handleOverviewOpenChange}
              containerStyle={styles.collapsible}>
              <Collapsible.Trigger hideDefaultIcon>
                {isOpen => (
                  <Stack direction="row" align="flex-start" justify="space-between" gap={scale(8)}>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                      style={styles.articleTitle}>
                      {overviewCopy.title}
                    </Text>
                    {renderChevron(isOpen)}
                  </Stack>
                )}
              </Collapsible.Trigger>
              <Collapsible.Content contentStyle={styles.bodyBlock}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  style={styles.bodyText}>
                  {overviewCopy.intro}
                  {'\n'}
                  {overviewCopy.checkpointLead}
                  {overviewCopy.achievementPinsLabel}
                  {overviewCopy.checkpointNames}
                  {overviewCopy.masteredPrefix}
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {overviewCopy.masteredPin}
                  </Text>
                  {overviewCopy.masteredSuffix}
                  {'\n'}
                  {overviewCopy.checkpointLead}
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {overviewCopy.achievementPinsLabel}
                    {overviewCopy.checkpointNames}
                  </Text>
                  {overviewCopy.masteredPrefix}
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {overviewCopy.masteredPin}
                  </Text>
                  {overviewCopy.masteredSuffix}
                </Text>
              </Collapsible.Content>
            </Collapsible>

            <Divider color={colors['3']} spacing={scale(20)} thickness={StyleSheet.hairlineWidth} />

            <Collapsible
              open={expandedArticle === 'method'}
              onOpenChange={handleMethodOpenChange}
              containerStyle={styles.collapsible}>
              <Collapsible.Trigger hideDefaultIcon>
                {isOpen => (
                  <Stack direction="row" align="flex-start" justify="space-between" gap={scale(12)}>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                      style={styles.articleTitle}>
                      {methodCopy.title} {methodCopy.subtitle}
                    </Text>
                    {renderChevron(isOpen)}
                  </Stack>
                )}
              </Collapsible.Trigger>
              <Collapsible.Content contentStyle={styles.methodContent}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  style={styles.bodyText}>
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {methodCopy.sections.playingGames.title}
                  </Text>
                  {'\n'}
                  {methodCopy.sections.playingGames.body}
                  {'\n'}
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {methodCopy.sections.developingForms.title}
                  </Text>
                  {'\n'}
                  {methodCopy.sections.developingForms.body}
                  {'\n'}
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.bodyText}>
                    {methodCopy.sections.assessingSkills.title}
                  </Text>
                  {'\n'}
                  {methodCopy.sections.assessingSkills.body}
                </Text>
              </Collapsible.Content>
            </Collapsible>
          </Stack>
        </Stack>
      </Scroll>

      <Stack
        paddingX={scale({ phone: 16, tablet: 20 })}
        paddingTop={scale({ phone: 12, tablet: 20 })}
        paddingBottom={bottomPadding}>
        <Stack
          direction="row"
          backgroundColor={withOpacity(colors.Error, 0.08)}
          style={styles.safetyCallout}>
          <SvgIcon name="IcWarning" size={scale(20)} />
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.safetyText}>
            {copy.safetyConcern}
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              onPress={handleContactPress}
              style={styles.contactLink}>
              {copy.contactUs}
            </Text>
          </Text>
        </Stack>
      </Stack>

      <GuardianContactUsPopup open={contactOpen} onClose={() => setContactOpen(false)} />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: scale({ phone: 16, tablet: 20 }),
    paddingTop: scale({ phone: 12, tablet: 16 }),
    paddingBottom: scale({ phone: 22, tablet: 32 }),
    flexGrow: 1,
  },
  screenTitle: {
    color: guardianColors['10'],
    marginBottom: scale(20),
  },
  articleCard: {
    borderRadius: scale(12),
    paddingHorizontal: scale({ phone: 12, tablet: 18 }),
    paddingVertical: scale({ phone: 10, tablet: 16 }),
  },
  collapsible: {
    alignSelf: 'stretch',
  },
  articleTitle: {
    flex: 1,
    flexShrink: 1,
    color: colors['10'],
  },
  bodyText: {
    color: colors['10'],
  },
  bodyBlock: {
    color: colors['10'],
    marginTop: scale({ phone: 20, tablet: 16 }),
  },
  methodContent: {
    marginTop: scale(22),
  },
  chevron: {
    flexShrink: 0,
  },
  chevronUp: {
    transform: [{ rotate: '-90deg' }],
  },
  chevronDown: {
    transform: [{ rotate: '90deg' }],
  },
  safetyCallout: {
    borderRadius: scale(12),
    borderWidth: scale(0.5),
    borderColor: colors['Level 3'],
    gap: scale(8),
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  safetyText: {
    flex: 1,
    color: colors['10'],
  },
  contactLink: {
    color: colors.Blue,
    textDecorationLine: 'underline',
  },
}));
